import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {format} from 'date-fns'
import { getCurrentUser } from '../utils/auth'
import './ModalNotes.css'

const SharedNotesModal = ({
    closeModal,
    entityInfo,
    title,
    fetchPath,
    createPath,
    createEntityKey,
}) => {
    const modalRef = useRef(null);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingNotes, setIsLoadingNotes] = useState(true);
    const [toast, setToast] = useState({ message: '', type: 'success' });

    const entityId = entityInfo?.id;

    const noteBy = useMemo(() => {
        const currentUser = getCurrentUser();
        if (!currentUser) {
            return '';
        }

        return `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim();
    }, []);

    const sortedNotes = useMemo(() => {
        return [...notes].sort((left, right) => {
            const leftTime = Date.parse(left?.note_date ?? '');
            const rightTime = Date.parse(right?.note_date ?? '');

            if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) {
                return 0;
            }
            if (Number.isNaN(leftTime)) {
                return 1;
            }
            if (Number.isNaN(rightTime)) {
                return -1;
            }

            return leftTime - rightTime;
        });
    }, [notes]);

    const loadNotes = useCallback(async ({showLoader = true} = {}) => {
        if (!entityId) {
            setNotes([]);
            setIsLoadingNotes(false);
            return;
        }

        try {
            if (showLoader) {
                setIsLoadingNotes(true);
            }

            const response = await fetch(process.env.REACT_APP_API_URL_BASE + fetchPath + entityId,
                {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                });

            if (!response.ok) {
                throw new Error('Failed to load notes');
            }

            const data = await response.json();
            setNotes(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
        } finally {
            if (showLoader) {
                setIsLoadingNotes(false);
            }
        }
    }, [entityId, fetchPath]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalOverscrollBehavior = document.body.style.overscrollBehavior;

        document.body.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'none';

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.overscrollBehavior = originalOverscrollBehavior;
        };
    }, []);

    useEffect(() => {
        if (isLoadingNotes) {
            return;
        }

        if (!modalRef.current) {
            return;
        }

        modalRef.current.scrollTop = modalRef.current.scrollHeight;
    }, [isLoadingNotes, sortedNotes.length]);

    useEffect(() => {
        if (!toast.message) {
            return;
        }

        const timerId = window.setTimeout(() => {
            setToast({ message: '', type: 'success' });
        }, 2200);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [toast]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedNote = newNote.trim();
        if (!trimmedNote || !entityId) {
            return;
        }

        const payload = {
            note: trimmedNote,
            note_by: noteBy,
            [createEntityKey]: entityId,
        };

        try {
            setIsSaving(true);
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + createPath,
                {
                    method: 'POST',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

            if (!response.ok) {
                throw new Error('Failed to save note');
            }

            await response.json();
            await loadNotes({showLoader: false});
            setNewNote('');
            setToast({ message: 'Note saved', type: 'success' });
        } catch (error) {
            console.error(error);
            setToast({ message: 'Could not save note', type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div
            className='protocol-notes-overlay'
            onClick={(e) => {
                if (e.target.className === 'protocol-notes-overlay') {
                    closeModal();
                }
            }}
        >
            <div className='protocol-notes-modal' ref={modalRef}>
                <button className='protocol-notes-close-btn' onClick={closeModal} aria-label='Close notes modal'>X</button>

                <header className='protocol-notes-header'>
                    <div className='protocol-notes-title'>{title}</div>
                </header>

                {toast.message ? (
                    <div className={`protocol-notes-toast protocol-notes-toast-${toast.type}`} role='status' aria-live='polite'>
                        {toast.message}
                    </div>
                ) : null}

                {isLoadingNotes ? (
                    <section className='protocol-notes-skeleton-wrap' aria-live='polite' aria-busy='true'>
                        <div className='protocol-notes-skeleton-line protocol-notes-skeleton-title'></div>
                        <div className='protocol-notes-skeleton-line protocol-notes-skeleton-row'></div>
                        <div className='protocol-notes-skeleton-line protocol-notes-skeleton-row'></div>
                        <div className='protocol-notes-skeleton-line protocol-notes-skeleton-row'></div>
                        <div className='protocol-notes-skeleton-line protocol-notes-skeleton-textarea'></div>
                        <div className='protocol-notes-skeleton-actions'>
                            <div className='protocol-notes-skeleton-line protocol-notes-skeleton-btn'></div>
                            <div className='protocol-notes-skeleton-line protocol-notes-skeleton-btn'></div>
                        </div>
                    </section>
                ) : (
                    <>
                        <section className='protocol-notes-card'>
                            {sortedNotes.length === 0 ? (
                                <div className='protocol-notes-empty'>No notes yet.</div>
                            ) : (
                                <ol className='protocol-notes-history-list'>
                                    {sortedNotes.map((note, index) => (
                                        <li className='protocol-notes-history-item' key={`${note.note_date ?? 'date'}-${note.note_by ?? 'by'}-${index}`}>
                                            <div className='protocol-notes-history-meta'>
                                                <span className='protocol-notes-history-author'>{note.note_by || 'Unknown user'}</span>
                                                <span className='protocol-notes-history-separator' aria-hidden='true'>•</span>
                                                <time dateTime={note.note_date}>{format(new Date(note.note_date), 'MM/dd/yyyy')}</time>
                                            </div>
                                            <p className='protocol-notes-history-body'>{note.note}</p>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </section>

                        <form className='protocol-notes-form' onSubmit={handleSubmit}>
                            <label htmlFor='new-entity-note'>New Note</label>
                            <textarea
                                id='new-entity-note'
                                name='note'
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                rows='4'
                                placeholder='Add a new note...'
                            />

                            <div className='protocol-notes-actions'>
                                <button type='button' className='protocol-notes-btn-secondary' onClick={closeModal} disabled={isSaving}>Close</button>
                                <button type='submit' className='protocol-notes-btn-primary' disabled={isSaving}>Save Note</button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    )
}

export default SharedNotesModal
